import React, { useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import Sidebar from './Layout/Sidebar/Sidebar';
import Header from './Layout/Header/Header';
import ApprovalBanner from './ApprovalBanner/ApprovalBanner';
import MultiProfileBanner from './MultiProfileBanner/MultiProfileBanner';
import EmailVerificationBanner from './EmailVerificationBanner/EmailVerificationBanner';
import styles from './mainlayout.module.css';
import { usePlanFeatures } from '../hooks/usePlanFeature';
import { subscribeProfileSwitchBroadcast } from '../lib/auth/authV2TabSync';

interface MainLayoutProps {
    children: React.ReactNode;
}

// AB#1675 — Persist the sidebar open/collapsed state in localStorage so the
// user's preference survives reloads. Default is OPEN so labels show on
// first landing; users who collapse it stay collapsed next session.
const SIDEBAR_STATE_KEY = 'devanza:sidebar:open';

const readInitialSidebarState = (): boolean => {
    try {
        const v = window.localStorage.getItem(SIDEBAR_STATE_KEY);
        if (v === null) return true; // default open
        return v === '1';
    } catch {
        return true;
    }
};

const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
    const [isSidebarOpen, setSidebarOpen] = useState<boolean>(readInitialSidebarState);
    const toggleSidebar = () => setSidebarOpen((prev) => {
        const next = !prev;
        try { window.localStorage.setItem(SIDEBAR_STATE_KEY, next ? '1' : '0'); } catch { /* noop */ }
        return next;
    });
    const queryClient = useQueryClient();

    // Warm the subscription cache so RequireFeature children render synchronously
    usePlanFeatures();

    // AB#1439 — cross-tab profile-switch sync. Every authenticated page that
    // mounts MainLayout subscribes to the BroadcastChannel; a switch in Tab A
    // triggers a state reset + reload in Tab B.
    useEffect(() => subscribeProfileSwitchBroadcast(queryClient), [queryClient]);

    // Theme is now owned by ThemeContext — it toggles `body.dark-theme` based
    // on the user's persisted preference (default: dark). The previous
    // unconditional `add('dark-theme')` here is intentionally removed.

    // AB#1675 — Azure DevOps-style shell: fixed-viewport flex column. Header
    // is sticky at top, then a horizontal row with the sidebar (its own
    // scroll) on the left and the content column (its own scroll) on the
    // right. Banners live inside the content column so they don't push the
    // chrome down.
    return (
        <div className={styles.shell}>
            <Header toggleSidebar={toggleSidebar} />
            <div className={`${styles.body} ${isSidebarOpen ? styles.sidebarOpen : styles.sidebarCollapsed}`}>
                <Sidebar isOpen={isSidebarOpen} onToggle={toggleSidebar} />
                <div className={styles.contentColumn}>
                    <ApprovalBanner />
                    {/* AB#1612 Phase 1A.2 — soft-gate email verification banner.
                        Self-gated: renders nothing when EmailVerified=true. */}
                    <EmailVerificationBanner />
                    {/* Auth v2 first-time multi-profile discovery banner (AB#1440). Self-gated. */}
                    <MultiProfileBanner />
                    <main className={styles.content}>
                        {children}
                    </main>
                </div>
            </div>
        </div>
    );
};

export default MainLayout;
